import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { SchemaEditor } from '@/components/schema-editor';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import type { CapabilityRevisionFile } from '@/types';
import { toast } from 'sonner';

const LOG_PREFIX = 'schema-detail-page';
const EMPTY_SCHEMA: Record<string, unknown> = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    properties: {},
    required: [],
};

function tryParseJson(value: unknown): Record<string, unknown> | null {
    if (!value) return null;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
            return null;
        } catch {
            return null;
        }
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return null;
}

function isMetadataPath(path: string | undefined): boolean {
    if (!path) return false;
    return path === 'metadata.json' || path.endsWith('/metadata.json');
}

function definitionFromMetadataFile(
    files: CapabilityRevisionFile[] | undefined,
): Record<string, unknown> | null {
    if (!files?.length) return null;
    const meta = files.find((f) => isMetadataPath(f.path));
    if (!meta) return null;
    const parsed = tryParseJson(meta.content_text);
    if (!parsed) return null;
    const fromDef = tryParseJson(parsed.definition);
    if (fromDef) return fromDef;
    // Allow a raw JSON Schema document at the root of metadata.json.
    if (parsed.type != null || parsed.properties != null) return parsed;
    return null;
}

function extractSchemaDefinition(candidate: Record<string, unknown> | null | undefined): Record<string, unknown> {
    if (!candidate) return EMPTY_SCHEMA;

    const typeConfig = tryParseJson(candidate.type_config) ?? {};
    const candidateDefinition =
        tryParseJson(typeConfig.definition) ??
        tryParseJson(candidate.definition) ??
        tryParseJson((candidate.config as Record<string, unknown> | undefined)?.definition) ??
        definitionFromMetadataFile(candidate.files as CapabilityRevisionFile[] | undefined);

    return candidateDefinition ?? EMPTY_SCHEMA;
}

function stringifySchema(value: Record<string, unknown>): string {
    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

function buildMetadataContent(
    existingText: string | null | undefined,
    definition: Record<string, unknown>,
): string {
    const existing = tryParseJson(existingText) ?? {};
    return JSON.stringify({ ...existing, definition }, null, 2);
}

export default function SchemaDetailPage() {
    const { appKey, name } = useParams();
    const queryClient = useQueryClient();
    const { data, isLoading } = useCapabilityDetail(appKey ?? '', name ?? '');
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const [currentSchema, setCurrentSchema] = useState<Record<string, unknown>>(EMPTY_SCHEMA);
    const [isSaving, setIsSaving] = useState(false);

    const immutableRevisions = useMemo(
        () => ((data as Record<string, unknown> | undefined)?.immutable_revisions ?? []) as Array<Record<string, unknown>>,
        [data],
    );

    const selectedImmutableRevision = useMemo(
        () => immutableRevisions.find((revision) => revision.id === selectedRevisionId),
        [immutableRevisions, selectedRevisionId],
    );

    const workingFiles = useMemo(() => {
        const wr = (data as Record<string, unknown> | undefined)?.working_revision as
            | { files?: CapabilityRevisionFile[] }
            | undefined;
        const topFiles = (data as { files?: CapabilityRevisionFile[] } | undefined)?.files;
        return wr?.files ?? topFiles ?? [];
    }, [data]);

    const sourceSchema = useMemo(() => {
        if (selectedRevisionId !== WORKING_COPY_ID && selectedImmutableRevision) {
            return extractSchemaDefinition(selectedImmutableRevision);
        }
        const working = (data as Record<string, unknown> | undefined)?.working_revision as
            | Record<string, unknown>
            | undefined;
        // Prefer working revision; fall back to top-level files when revision payload omits them.
        if (working && !working.files && workingFiles.length > 0) {
            return extractSchemaDefinition({ ...working, files: workingFiles });
        }
        return extractSchemaDefinition(working);
    }, [data, selectedImmutableRevision, selectedRevisionId, workingFiles]);

    const sourceSchemaSerialized = useMemo(() => stringifySchema(sourceSchema), [sourceSchema]);

    useEffect(() => {
        setCurrentSchema(sourceSchema);
    }, [sourceSchemaSerialized, sourceSchema]);

    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;
    const hasWorkingRevision = Boolean((data as Record<string, unknown> | undefined)?.working_revision);
    const isArchived = (((data as { status?: string } | undefined)?.status ?? '').toLowerCase() === 'archived');
    const readOnly = isViewingPublished || !hasWorkingRevision || isArchived || isSaving;

    const isDirty = useMemo(
        () => !readOnly && stringifySchema(currentSchema) !== sourceSchemaSerialized,
        [currentSchema, readOnly, sourceSchemaSerialized],
    );

    const persistSchema = useCallback(
        async (schemaToSave: Record<string, unknown>) => {
            if (!appKey || !name) return;
            setIsSaving(true);
            try {
                // Upsert replaces the full file set when files are provided, so preserve
                // existing revision files and only rewrite metadata.json's definition.
                const existingMeta = workingFiles.find((f) => isMetadataPath(f.path));
                const metadataContent = buildMetadataContent(existingMeta?.content_text, schemaToSave);
                const filesPayload =
                    workingFiles.length > 0
                        ? workingFiles.map((f) => ({
                              path: f.path,
                              content: isMetadataPath(f.path)
                                  ? metadataContent
                                  : (f.content_text ?? ''),
                              contentType: f.content_type || 'application/json',
                          }))
                        : [
                              {
                                  path: 'metadata.json',
                                  content: metadataContent,
                                  contentType: 'application/json',
                              },
                          ];

                if (
                    workingFiles.length > 0 &&
                    !filesPayload.some((f) => isMetadataPath(f.path))
                ) {
                    filesPayload.push({
                        path: 'metadata.json',
                        content: metadataContent,
                        contentType: 'application/json',
                    });
                }

                await api.post(
                    `/applications/${appKey}/capabilities/${encodeURIComponent(name)}/upsert`,
                    {
                        targetRevision: 'working',
                        type_config: {
                            definition: schemaToSave,
                        },
                        files: filesPayload,
                    },
                );
                await queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
                toast.success('Schema saved');
                logger.info(`${LOG_PREFIX}.persistSchema: schema updated`, { name });
            } catch (error) {
                logger.error(`${LOG_PREFIX}.persistSchema: save failed`, { error });
                toast.error('Failed to save schema', {
                    description: 'Could not save schema changes. Try again.',
                });
                throw error;
            } finally {
                setIsSaving(false);
            }
        },
        [appKey, name, queryClient, workingFiles],
    );

    const handleSave = useCallback(async () => {
        if (!isDirty) return;
        await persistSchema(currentSchema);
    }, [currentSchema, isDirty, persistSchema]);

    const handleSchemaEditorSave = useCallback(
        async (updatedSchema: { schema_definition: Record<string, unknown> }) => {
            if (readOnly) return;
            const schemaToSave = updatedSchema.schema_definition;
            setCurrentSchema(schemaToSave);
            await persistSchema(schemaToSave);
        },
        [persistSchema, readOnly],
    );

    const saveRef = useRef(handleSave);
    saveRef.current = handleSave;
    const stableSave = useCallback(async () => saveRef.current(), []);

    const supplementalDeferredSave = useMemo(
        () => ({
            isDirty,
            isSaving,
            save: stableSave,
        }),
        [isDirty, isSaving, stableSave],
    );

    return (
        <CapabilityDetailLayout
            typeLabel="Schema"
            appKey={appKey}
            capabilityName={name}
            selectedRevisionId={selectedRevisionId}
            onRevisionChange={setSelectedRevisionId}
            supplementalDeferredSave={supplementalDeferredSave}
            overviewFormDisabled={readOnly}
        >
            {isLoading ? (
                <div className="p-6">Loading schema...</div>
            ) : (
                <div className="h-[72vh] min-h-[540px] rounded-lg border border-slate-200 bg-white">
                    <SchemaEditor
                        schema={{
                            id: (data as { id?: string } | undefined)?.id,
                            name: name ?? 'schema',
                            description: ((data as { description?: string } | undefined)?.description ?? '') as string,
                            schema_definition: currentSchema,
                        }}
                        onSchemaChange={setCurrentSchema}
                        onSave={handleSchemaEditorSave}
                        readOnly={readOnly}
                    />
                </div>
            )}
        </CapabilityDetailLayout>
    );
}
